import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceMessageRole,
  CaraWorkspacePlanStageState,
  CaraWorkspacePlanStageType
} from '@oysterjs/ais/api/graphql';
import { parseUsageValue } from '@oysterjs/ais/lib/usage';

export type ConversationItem =
  | CaraWorkspaceFieldsFragment['messages'][0]
  | CaraWorkspaceFieldsFragment['planStages'][0]
  | CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

export type ConversationGroup = {
  role: CaraWorkspaceMessageRole;
  items: ConversationItem[];
};

/**
 * Returns the timestamp used to sort items chronologically in the conversation.
 * Any stage that has ended sorts by endedAt so it lands after its own events;
 * in-progress stages sort by createdAt so they appear at the start of their run.
 */
export const itemTimestamp = (item: ConversationItem): Date | null => {
  if (item.__typename === 'CaraWorkspaceMessage') return new Date(item.timestamp);
  if (item.__typename === 'CaraWorkspacePlanStageEvent') return new Date(item.startedAt);
  if (item.__typename === 'CaraWorkspacePlanStage') {
    return new Date(item.endedAt ?? item.createdAt);
  }
  return null;
};

/**
 * Plan-stage items surface in the timeline in two cases:
 * - All ProcessAttachments stages, regardless of state. In-progress ones render a
 *   visible component; succeeded and failed ones produce no entry but still occupy a
 *   slot in the agent group so the "Thinking..." pending fallback can fire between
 *   ProcessAttachments completing and the first Task event arriving.
 * - Task stages that have failed (stage boundary in the render plan).
 */
const shouldRenderPlanStage = (stage: CaraWorkspaceFieldsFragment['planStages'][0]): boolean => {
  if (stage.type === CaraWorkspacePlanStageType.ProcessAttachments) return true;
  return (
    stage.type === CaraWorkspacePlanStageType.Task &&
    stage.state === CaraWorkspacePlanStageState.Failed
  );
};

/**
 * Non-message items render under the agent's column. Messages keep their own role.
 */
const roleForItem = (item: ConversationItem): CaraWorkspaceMessageRole =>
  item.__typename === 'CaraWorkspaceMessage' ? item.role : CaraWorkspaceMessageRole.Agent;

/**
 * Combine messages, renderable plan stages, and ALL plan-stage events into a single
 * chronologically-sorted timeline, then group consecutive items by speaker (role).
 *
 * Note: events from every plan stage are included even when the parent stage itself
 * is filtered out by `shouldRenderPlanStage`. Only the stage-level entry is gated;
 * the per-event timeline always renders.
 */
export const buildGroups = (workspace: CaraWorkspaceFieldsFragment): ConversationGroup[] => {
  const combined: ConversationItem[] = [
    ...workspace.messages,
    ...workspace.planStages.filter(shouldRenderPlanStage),
    ...workspace.planStages.flatMap((p) => p.events)
  ].sort((a, b) => (itemTimestamp(a)?.getTime() || 0) - (itemTimestamp(b)?.getTime() || 0));

  const groups: ConversationGroup[] = [];
  combined.forEach((item) => {
    const role = roleForItem(item);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.role === role) {
      lastGroup.items.push(item);
    } else {
      groups.push({ role, items: [item] });
    }
  });

  return groups;
};

/**
 * Collect the IDs of follow-up suggestions the user has already chosen, so the
 * UI can dim/hide them in subsequent agent responses.
 */
export const collectUsedFollowUpIds = (workspace: CaraWorkspaceFieldsFragment): Set<string> => {
  const usedFollowUpIds = new Set<string>();
  workspace.messages.forEach((message) => {
    if (message.role === CaraWorkspaceMessageRole.User && message.selectedFollowUp?.id) {
      usedFollowUpIds.add(message.selectedFollowUp.id);
    }
  });
  return usedFollowUpIds;
};

/**
 * Returns confirmed platform units for the TASK stage that produced this agent
 * message (matched via the preceding user message → initiatedByMessageId).
 */
export const platformUnitsUsedForAgentMessage = (
  workspace: CaraWorkspaceFieldsFragment,
  messageId: string
): number | null => {
  const messages = workspace.messages;
  const messageIndex = messages.findIndex((m) => m.id === messageId);
  if (messageIndex < 0) {
    return null;
  }

  let initiatingUserMessageId: string | null = null;
  for (let i = messageIndex - 1; i >= 0; i--) {
    if (messages[i]?.role === CaraWorkspaceMessageRole.User) {
      initiatingUserMessageId = messages[i]!.id;
      break;
    }
  }
  if (!initiatingUserMessageId) {
    return null;
  }

  const taskStage = workspace.planStages.find(
    (stage) =>
      stage.type === CaraWorkspacePlanStageType.Task &&
      stage.initiatedByMessageId === initiatingUserMessageId
  );
  return taskStage?.platformUnitsUsed == null ? null : parseUsageValue(taskStage.platformUnitsUsed);
};
